import type { SessionNextStep } from "@/domains/auth/session/types";

interface PersonalOnboardingEntry {
  /** Dove la sessione dice che l'utente deve andare. */
  nextStep: SessionNextStep | null | undefined;
  /** `onboarding_status === "completed"` sul profilo personale. */
  profileCompleted: boolean;
  /** La festa di fine onboarding è in corso: nessuno la interrompe. */
  celebrating: boolean;
}

/**
 * Se `/auth/onboarding` è ancora la schermata giusta per chi la sta chiedendo.
 *
 * La rotta guardava una cosa sola, `onboarding_status`, che è lo stato del
 * profilo **personale**. Ma chi crea un'azienda non compila mai quel profilo:
 * il suo `onboarding_status` resta `in_progress` per sempre, e digitando l'URL
 * si ritrovava davanti all'onboarding freelance — "Come vuoi creare il tuo
 * profilo?", importa da LinkedIn, carica il curriculum — a workspace già fatto.
 *
 * La sessione lo sapeva già: `deriveNextStep` salta il ramo
 * `complete_personal_profile` appena esiste una membership aziendale, e
 * risponde `publisher_dashboard`. `ProtectedRoute` però non forza i `next_step`
 * terminali — se lo facesse, un publisher non potrebbe visitare nient'altro che
 * la sua dashboard — quindi la porta la deve chiudere la rotta di destinazione,
 * che è l'unica a sapere per chi è stata scritta.
 *
 * Due casi che *non* vanno respinti, e sono il motivo per cui questa non è una
 * disuguaglianza secca:
 * - **la festa**: completare l'onboarding cambia `next_step` sotto i piedi
 *   dello schermo di successo, che è il premio di una coreografia da ~10s
 *   (SPEC AC 15/18). Finché dura, la guardia tace.
 * - **le sessioni senza `next_step`**: bootstrap più vecchi del campo. Lì
 *   l'unica cosa nota resta lo stato del profilo, ed è quella che decide.
 */
export const canEnterPersonalOnboarding = (
  entry: PersonalOnboardingEntry,
): boolean => {
  if (entry.celebrating) return true;
  if (entry.profileCompleted) return false;
  if (!entry.nextStep) return true;
  return entry.nextStep === "complete_personal_profile";
};
